import { Fragment, useState, useEffect, useContext } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, DocumentTextIcon, CalendarIcon, CurrencyDollarIcon, UserIcon, InformationCircleIcon, ArrowDownTrayIcon, CodeBracketIcon, ArrowPathIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline'
import { colorMap, formatDate } from '../../Common'
import { AuthContextApi } from '../../Context/api'
import { toast } from 'react-toastify'

const ModalVisualizacaoFatura = ({ isOpen, onClose, fatura, color = 'purple', downloadFaturaPDF, geracaoNovaFatura }) => {
    const colorClasses = colorMap[color] || colorMap.purple
    const { getFatura } = useContext(AuthContextApi)
    const [faturaCompleta, setFaturaCompleta] = useState(null)
    const [loading, setLoading] = useState(false)
    const [downloadLoading, setDownloadLoading] = useState(false)
    const [geracaoLoading, setGeracaoLoading] = useState(false)

    // Estado para controlar o modal de seleção de download
    const [showDownloadModal, setShowDownloadModal] = useState(false)
    const [downloadData, setDownloadData] = useState(null)
    const [downloadType, setDownloadType] = useState(null) // 'download' ou 'geracao'
    const [baseFileName, setBaseFileName] = useState('')

    useEffect(() => {
        if (isOpen && fatura && fatura.id) {
            loadFaturaCompleta()
        } else {
            setFaturaCompleta(null)
        }
    }, [isOpen, fatura])

    const loadFaturaCompleta = async () => {
        setLoading(true)
        const dados = await getFatura(fatura.id)
        if (dados) {
            setFaturaCompleta(dados)
        }
        setLoading(false)
    }

    if (!fatura && !faturaCompleta) return null

    const dadosExibir = faturaCompleta || fatura

    const formatCurrency = (value) => {
        return parseFloat(value / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        })
    }

    const formatCNPJ = (cnpj) => {
        if (!cnpj) return '-'
        return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    }

    const formatPhone = (phone) => {
        if (!phone) return '-'
        return phone.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3')
    }

    const downloadFile = (base64Data, fileName, mimeType) => {
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: mimeType })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(link.href)
    }

    // Chama a API de download e abre o modal de seleção
    const handleDownload = async () => {
        if (!dadosExibir.id) {
            toast.error('ID da fatura não disponível')
            return
        }

        setDownloadLoading(true)
        try {
            const response = await downloadFaturaPDF(dadosExibir.id)
            if (response) {
                const fileName = dadosExibir.arquivo_nome?.replace(/\.[^/.]+$/, '') || `fatura_${dadosExibir.numero_referencia}`
                setDownloadData(response)
                setBaseFileName(fileName)
                setDownloadType('download')
                setShowDownloadModal(true)
            } else {
                toast.error('Erro ao obter os arquivos')
            }
        } catch (error) {
            toast.error('Erro ao fazer download')
        } finally {
            setDownloadLoading(false)
        }
    }

    // Chama a API de geração e abre o modal de seleção
    const handleGerarNovaVersao = async () => {
        if (!dadosExibir.id) {
            toast.error('ID da fatura não disponível')
            return
        }

        if (!geracaoNovaFatura) {
            toast.error('Função de geração não disponível')
            return
        }

        setGeracaoLoading(true)
        try {
            const response = await geracaoNovaFatura(dadosExibir.id)

            if (response) {
                const fileName = dadosExibir.arquivo_nome?.replace(/\.[^/.]+$/, '') || `fatura_${dadosExibir.numero_referencia}`
                setDownloadData(response)
                setBaseFileName(`${fileName}_nova_versao`)
                setDownloadType('geracao')
                setShowDownloadModal(true)
            } else {
                toast.error('Erro ao gerar nova versão')
            }
        } catch (error) {
            console.error('Erro ao gerar nova versão:', error)
            toast.error('Erro ao gerar nova versão da fatura')
        } finally {
            setGeracaoLoading(false)
        }
    }

    // Executa o download baseado na seleção do usuário
    const executeDownload = (type) => {
        if (!downloadData) return

        if (type === 'pdf' || type === 'ambos') {
            if (downloadData.pdf) {
                downloadFile(downloadData.pdf, `${baseFileName}.pdf`, 'application/pdf')
            }
        }

        if (type === 'xml' || type === 'ambos') {
            if (downloadData.xml) {
                downloadFile(downloadData.xml, `${baseFileName}.xml`, 'application/xml')
            }
        }

        const successMessage = downloadType === 'geracao'
            ? 'Nova versão gerada e download iniciado!'
            : 'Download iniciado com sucesso!'

        toast.success(successMessage)
        closeDownloadModal()
    }

    const closeDownloadModal = () => {
        setShowDownloadModal(false)
        setDownloadData(null)
        setDownloadType(null)
        setBaseFileName('')
    }

    return (
        <>
            <Transition.Root show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white px-4 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                                    <div className="absolute right-0 top-0 pr-4 pt-4">
                                        <button
                                            type="button"
                                            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
                                            onClick={onClose}
                                        >
                                            <span className="sr-only">Fechar</span>
                                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                        </button>
                                    </div>

                                    <div className="border-b border-gray-200 pb-4">
                                        <div className={`inline-flex items-center justify-center w-full px-4 py-2 rounded-lg ${colorClasses.containerBg} mb-3`}>
                                            <DocumentTextIcon className="h-5 w-5 mr-2" />
                                            <span className="text-sm font-semibold text-gray-700">Visualização de Fatura</span>
                                        </div>
                                        <h3 className={`text-2xl font-bold leading-7 text-center ${colorClasses.textGradient}`}>
                                            Fatura {dadosExibir.numero_referencia}
                                        </h3>
                                        <p className="mt-2 text-sm text-gray-500 text-center">
                                            {formatDate(dadosExibir.data_emissao)}
                                        </p>
                                        <div className="mt-4 flex justify-center gap-3">
                                            {/* Botão Download */}
                                            <button
                                                onClick={handleDownload}
                                                disabled={downloadLoading}
                                                className={`inline-flex items-center px-4 py-2 rounded-lg ${colorClasses.headerButtonBg} text-white font-medium text-sm shadow-sm hover:opacity-90 transition-all duration-200 disabled:opacity-50`}
                                            >
                                                {downloadLoading ? (
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                ) : (
                                                    <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                                                )}
                                                Download
                                            </button>

                                            {/* Botão Nova Versão */}
                                            {geracaoNovaFatura && (
                                                <button
                                                    onClick={handleGerarNovaVersao}
                                                    disabled={geracaoLoading}
                                                    className={`inline-flex items-center px-4 py-2 rounded-lg bg-green-600 text-white font-medium text-sm shadow-sm hover:bg-green-700 transition-all duration-200 disabled:opacity-50`}
                                                >
                                                    {geracaoLoading ? (
                                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                    ) : (
                                                        <ArrowPathIcon className="w-5 h-5 mr-2" />
                                                    )}
                                                    Nova Versão
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-6 space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                                        {loading ? (
                                            <div className="flex items-center justify-center py-12">
                                                <div className="text-center">
                                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                                                    <p className="mt-4 text-gray-600">Carregando detalhes...</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="border-b border-gray-200 pb-6">
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <UserIcon className="h-5 w-5 text-purple-600" />
                                                        <h4 className="text-base font-semibold text-gray-900">Informações do Cliente</h4>
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">Nome Empresarial</label>
                                                            <p className="text-sm font-medium text-gray-900">{dadosExibir.cliente_nome_empresarial || '-'}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">CNPJ</label>
                                                            <p className="text-sm font-medium text-gray-900">{formatCNPJ(dadosExibir.cliente_cnpj)}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                                                            <p className="text-sm font-medium text-gray-900">{dadosExibir.cliente_email || '-'}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">Telefone</label>
                                                            <p className="text-sm font-medium text-gray-900">{formatPhone(dadosExibir.cliente_telefone)}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="border-b border-gray-200 pb-6">
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <BuildingOfficeIcon className="h-5 w-5 text-purple-600" />
                                                        <h4 className="text-base font-semibold text-gray-900">Empresa Responsável pelo Pagamento</h4>
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                                                            <label className="block text-xs font-medium text-purple-700 mb-1">Nome da Empresa</label>
                                                            <p className="text-sm font-semibold text-gray-900">{dadosExibir.pagamento_nome_empresa || '-'}</p>
                                                        </div>
                                                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                                                            <label className="block text-xs font-medium text-purple-700 mb-1">CNPJ</label>
                                                            <p className="text-sm font-semibold text-gray-900">{formatCNPJ(dadosExibir.pagamento_cnpj)}</p>
                                                        </div>
                                                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                                                            <label className="block text-xs font-medium text-purple-700 mb-1">Titular da Conta</label>
                                                            <p className="text-sm font-semibold text-gray-900">{dadosExibir.pagamento_nome_titular || '-'}</p>
                                                        </div>
                                                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                                                            <label className="block text-xs font-medium text-purple-700 mb-1">Chave PIX</label>
                                                            <p className="text-sm font-semibold text-gray-900 break-all">{dadosExibir.pagamento_chave_pix || '-'}</p>
                                                        </div>
                                                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                                                            <label className="block text-xs font-medium text-purple-700 mb-1">E-mail</label>
                                                            <p className="text-sm font-semibold text-gray-900 break-all">{dadosExibir.pagamento_email || '-'}</p>
                                                        </div>
                                                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                                                            <label className="block text-xs font-medium text-purple-700 mb-1">Telefone</label>
                                                            <p className="text-sm font-semibold text-gray-900">{formatPhone(dadosExibir.pagamento_telefone)}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="border-b border-gray-200 pb-6">
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <CalendarIcon className="h-5 w-5 text-purple-600" />
                                                        <h4 className="text-base font-semibold text-gray-900">Datas</h4>
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">Data da Fatura</label>
                                                            <p className="text-sm font-medium text-gray-900">{formatDate(dadosExibir.data_fatura)}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">Data de Emissão</label>
                                                            <p className="text-sm font-medium text-gray-900">{formatDate(dadosExibir.data_emissao)}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">Data de Criação</label>
                                                            <p className="text-sm font-medium text-gray-900">{formatDate(dadosExibir.data_criacao)}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {faturaCompleta && faturaCompleta.itens && faturaCompleta.itens.length > 0 && (
                                                    <div className="border-b border-gray-200 pb-6">
                                                        <div className="flex items-center gap-3 mb-4">
                                                            <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                                                            <h4 className="text-base font-semibold text-gray-900">Itens da Fatura</h4>
                                                        </div>
                                                        <div className="space-y-3">
                                                            {faturaCompleta.itens.map((item, index) => (
                                                                <div key={item.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        <div>
                                                                            <p className="text-sm font-semibold text-gray-900">{item.servico_nome}</p>
                                                                            <p className="text-xs text-gray-600 mt-1">{item.servico_descricao}</p>
                                                                            <p className="text-xs text-gray-500 mt-2">
                                                                                <strong>Código:</strong> {item.servico_codigo_servico}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500 mt-1">
                                                                                <strong>Período:</strong> {formatDate(item.data_inicial)} - {formatDate(item.data_final)}
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex flex-col justify-between">
                                                                            <div className="grid grid-cols-3 gap-2 text-xs">
                                                                                <div>
                                                                                    <p className="text-gray-500">Quantidade</p>
                                                                                    <p className="font-semibold text-gray-900">{item.quantidade || 1}</p>
                                                                                </div>
                                                                                <div>
                                                                                    <p className="text-gray-500">Valor Unit.</p>
                                                                                    <p className="font-semibold text-gray-900">{formatCurrency(item.preco || item.valor || 0)}</p>
                                                                                </div>
                                                                                <div>
                                                                                    <p className="text-gray-500">Subtotal</p>
                                                                                    <p className="font-bold text-purple-700">
                                                                                        {formatCurrency((item.quantidade || 1) * (item.preco || item.valor || 0))}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                            {item.observacao && (
                                                                                <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-md p-2">
                                                                                    <p className="text-xs font-medium text-yellow-800">Observação:</p>
                                                                                    <p className="text-xs text-yellow-900 mt-1">{item.observacao}</p>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="border-b border-gray-200 pb-6">
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <CurrencyDollarIcon className="h-5 w-5 text-purple-600" />
                                                        <h4 className="text-base font-semibold text-gray-900">Valor</h4>
                                                    </div>
                                                    <div className="bg-purple-50 rounded-lg p-6 border-2 border-purple-200">
                                                        <label className="block text-sm font-medium text-purple-700 mb-2">Valor Total da Fatura</label>
                                                        <p className="text-3xl font-bold text-purple-900">{formatCurrency(dadosExibir.total)}</p>
                                                    </div>
                                                </div>

                                                <div className="border-b border-gray-200 pb-6">
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <InformationCircleIcon className="h-5 w-5 text-purple-600" />
                                                        <h4 className="text-base font-semibold text-gray-900">Informações Adicionais</h4>
                                                    </div>
                                                    <div className="grid grid-cols-1 gap-4">
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">Número de Referência</label>
                                                            <p className="text-sm font-medium text-gray-900">{dadosExibir.numero_referencia}</p>
                                                        </div>
                                                        {dadosExibir.arquivo_nome && (
                                                            <div className="bg-gray-50 rounded-lg p-4">
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Nome do Arquivo</label>
                                                                <p className="text-sm font-medium text-gray-900">{dadosExibir.arquivo_nome}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {dadosExibir.observacao && (
                                                    <div>
                                                        <div className="flex items-center gap-3 mb-4">
                                                            <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                                                            <h4 className="text-base font-semibold text-gray-900">Observações</h4>
                                                        </div>
                                                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{dadosExibir.observacao}</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className="mt-6 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all"
                                        >
                                            Fechar
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Modal de Seleção de Download */}
            <Transition.Root show={showDownloadModal} as={Fragment}>
                <Dialog as="div" className="relative z-[60]" onClose={closeDownloadModal}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-[60] overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white px-6 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                                    <div className="absolute right-0 top-0 pr-4 pt-4">
                                        <button
                                            type="button"
                                            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                            onClick={closeDownloadModal}
                                        >
                                            <span className="sr-only">Fechar</span>
                                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                        </button>
                                    </div>

                                    <div className="text-center mb-6">
                                        <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${downloadType === 'geracao' ? 'bg-green-100' : 'bg-purple-100'} mb-4`}>
                                            {downloadType === 'geracao' ? (
                                                <ArrowPathIcon className="h-6 w-6 text-green-600" />
                                            ) : (
                                                <ArrowDownTrayIcon className="h-6 w-6 text-purple-600" />
                                            )}
                                        </div>
                                        <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                                            {downloadType === 'geracao' ? 'Nova Versão Gerada!' : 'Arquivos Disponíveis'}
                                        </Dialog.Title>
                                        <p className="mt-2 text-sm text-gray-500">
                                            Selecione qual formato deseja baixar:
                                        </p>
                                    </div>

                                    <div className="space-y-3">
                                        {downloadData?.pdf && (
                                            <button
                                                onClick={() => executeDownload('pdf')}
                                                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border-2 border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-300 transition-all duration-200"
                                            >
                                                <DocumentTextIcon className="w-6 h-6" />
                                                <span className="font-medium">Baixar PDF</span>
                                            </button>
                                        )}

                                        {downloadData?.xml && (
                                            <button
                                                onClick={() => executeDownload('xml')}
                                                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border-2 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all duration-200"
                                            >
                                                <CodeBracketIcon className="w-6 h-6" />
                                                <span className="font-medium">Baixar XML</span>
                                            </button>
                                        )}

                                        {downloadData?.pdf && downloadData?.xml && (
                                            <button
                                                onClick={() => executeDownload('ambos')}
                                                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border-2 border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-300 transition-all duration-200"
                                            >
                                                <ArrowDownTrayIcon className="w-6 h-6" />
                                                <span className="font-medium">Baixar Ambos</span>
                                            </button>
                                        )}
                                    </div>

                                    <div className="mt-6">
                                        <button
                                            type="button"
                                            onClick={closeDownloadModal}
                                            className="w-full inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-all"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    )
}

export default ModalVisualizacaoFatura

